import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { ContractMessage, ContractState } from '../../../../types/ContractMessage';
import { isEmptyFilterValue } from '../../../cdl/Filter/helper/isEmptyFilterValue';
import { CenteredPagination } from '../../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { Text } from '../../../cdl/Text/Text';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { DeleteConfirmPopup } from '../../../common/DeleteConfirmPopup/DeleteConfirmPopup';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../../common/helpers/formatDate.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { ListingWithMore } from '../../../common/ListingWithMore/ListingWithMore';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../common/ui/Box';
import { ContractRowActionsMenu } from '../../../contracts/overview/components/ContractRowActionsMenu';
import { ContractsEmptyState } from '../../../contracts/overview/components/ContractsEmptyState';
import { ContractsNoResultsState } from '../../../contracts/overview/components/ContractsNoResultsState';
import { ContractStatusTag } from '../../../contracts/overview/components/ContractStatusTag';
import { ContractTableExpirationDisplay } from '../../../contracts/overview/components/ContractTableExpirationDisplay';
import { ContractTableValidityDisplay } from '../../../contracts/overview/components/ContractTableValidityDisplay';
import { LUBES_CONTRACT_FILTER_KEYS } from '../../../contracts/overview/contractsFilterKeys';
import { useContractDelete } from '../../../contracts/overview/hooks/useContractDelete';
import { useContractsPagination } from '../../../contracts/overview/hooks/useContractsPagination';

export const ContractsTable = () => {
    const search = useSearch({ from: '/_app/contracts/' });
    const navigate = useNavigate({ from: '/contracts' });
    const { addToast, addErrorToast } = useToasts();
    const role = useRole();
    const deleteMutation = useContractDelete();
    const [contractToDelete, setContractToDelete] = useState<ContractMessage | null>(null);
    const hasActiveFilters = LUBES_CONTRACT_FILTER_KEYS.some((key) => !isEmptyFilterValue(search[key]));
    const hasAdminRights = role.hasAnyAdminRights();

    const paginationQuery = useContractsPagination({
        page: search.page,
        states: search.states,
        validUntilFrom: search.validUntilFrom,
        validUntilTo: search.validUntilTo,
        supplierIds: search.supplierIds,
        customerGroupIds: search.customerGroupId ? [search.customerGroupId] : undefined,
        customerIds: search.customerIds,
        evergreen: search.evergreen,
        searchQuery: search.searchQuery,
    });

    const handleDeleteConfirm = () => {
        if (!contractToDelete) return;

        deleteMutation.mutate(contractToDelete.id, {
            onSuccess: () => {
                addToast(translate('contracts.deleteSuccessToast'));
                setContractToDelete(null);
            },
            onError: () => {
                addErrorToast(translate('contracts.deleteErrorToast'));
            },
        });
    };

    if (paginationQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (paginationQuery.isError) {
        return <AdvancedError />;
    }

    if (paginationQuery.data.content.length === 0) {
        return hasActiveFilters ? <ContractsNoResultsState /> : <ContractsEmptyState />;
    }

    return (
        <>
            <TableBuilder
                data={paginationQuery.data.content}
                grow={false}
                isLoading={paginationQuery.isPlaceholderData && paginationQuery.isFetching}
                isRowSubtle={(contract) => contract.state === ContractState.INACTIVE}
                onRowSelect={(row) => navigate({ to: '/contracts/$id', params: { id: row.id } })}
            >
                <TableBuilderColumn<ContractMessage> header={translate('contracts.labels.name')} width="20%" truncate>
                    {(contract) => <Text fontWeight={600}>{contract.name}</Text>}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage>
                    header={translate('contracts.labels.supplier')}
                    width="20%"
                    truncate
                >
                    {(contract) => formatCompanyName({ company: contract.supplier })}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage>
                    header={translate(role.isSuperAdmin() ? 'contracts.labels.customer' : 'contracts.labels.company')}
                    width="20%"
                    truncate
                >
                    {(contract) => {
                        const customerNames = contract.customers
                            .map((customer) => formatCompanyName({ company: customer }))
                            .filter((customerName): customerName is string => !!customerName);

                        return <ListingWithMore>{customerNames}</ListingWithMore>;
                    }}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage> header={translate('contracts.labels.validity')} width="20%">
                    {(contract) => <ContractTableValidityDisplay contract={contract} />}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage>
                    header={translate('contracts.labels.expiration')}
                    width="10%"
                    numeric
                >
                    {(contract) => <ContractTableExpirationDisplay contract={contract} />}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage> header={translate('contracts.labels.status')} width="5%">
                    {(contract) => <ContractStatusTag status={contract.state} />}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage>
                    header={translate('contracts.labels.lastModified')}
                    width="10%"
                    numeric
                >
                    {(contract) => (contract.dateUpdated ? formatDate({ date: contract.dateUpdated }) : null)}
                </TableBuilderColumn>
                <TableBuilderColumn<ContractMessage> header="" width="5%" interactive>
                    {(contract) => (
                        <Box display="flex" justifyContent="flex-end">
                            {hasAdminRights ? (
                                <ContractRowActionsMenu contract={contract} onDelete={setContractToDelete} />
                            ) : null}
                        </Box>
                    )}
                </TableBuilderColumn>
            </TableBuilder>

            <CenteredPagination
                currentPage={search.page}
                pageTotal={paginationQuery.pageTotal}
                onPageChange={(page) => {
                    navigate({ search: (previousSearch) => ({ ...previousSearch, page }) });
                }}
            />

            <DeleteConfirmPopup
                isOpen={!!contractToDelete}
                onSubmit={handleDeleteConfirm}
                onDismiss={() => setContractToDelete(null)}
                headline={translate('contracts.deleteConfirmTitle')}
                text={translate('contracts.deleteConfirmDescription')}
                itemToDelete={contractToDelete?.name}
                isSubmitting={deleteMutation.isPending}
            />
        </>
    );
};
